/**
 * Telegram Mini App: ask user to allow the bot to send private messages.
 * Стан throttling / one-shot post-map — у БД (user_onboarding_kv + onboarding_completed у GET).
 * @see https://core.telegram.org/bots/webapps — requestWriteAccess (Bot API 6.9+)
 */

import type { QueryClient } from "@tanstack/react-query";
import { patchOnboardingStateOnServer, type OnboardingState } from "../net/onboardingApi";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function getWebApp() {
  return typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;
}

function allowsWriteToPm(): boolean {
  const u = getWebApp()?.initDataUnsafe?.user as { allows_write_to_pm?: boolean } | undefined;
  return !!u?.allows_write_to_pm;
}

function canUseWriteAccessApi(): boolean {
  const wa = getWebApp();
  return !!(wa && typeof wa.isVersionAtLeast === "function" && wa.isVersionAtLeast("6.9"));
}

function fireRequestWriteAccess(qc: QueryClient, delayMs: number): void {
  window.setTimeout(() => {
    const wa = getWebApp();
    if (!wa?.requestWriteAccess) return;
    void (async () => {
      try {
        await patchOnboardingStateOnServer({ write_access_last_prompted_at_ms: Date.now() });
        await qc.invalidateQueries({ queryKey: ["onboarding-state"] });
      } catch {
        return;
      }
      try {
        // Allowed: the server confirms with its one-time thank-you message.
        wa.requestWriteAccess((granted) => {
          if (!granted) return;
          // Loaded here: the API client needs a browser (this module is also used in tests).
          void import("../../client")
            .then(({ verifyNotificationsUserNotificationsVerifyPost }) => verifyNotificationsUserNotificationsVerifyPost({ body: { source: "reminder" } }))
            .then(() => qc.invalidateQueries({ queryKey: ["userData"] }))
            .catch(() => { /* asked again next week at worst */ });
        });
      } catch {
        /* ignore */
      }
    })();
  }, delayMs);
}

/**
 * Повертаючі гравці: не частіше ніж раз на 7 днів після завантаження.
 * Пропускає «сирих» новачків і вікно, коли мап-місяць уже записаний, а нагороду ще не забрано.
 */
export function requestWriteAccessWeeklyOnGameSession(qc: QueryClient, verified: boolean): void {
  const snap = qc.getQueryData<OnboardingState>(["onboarding-state"]);
  if (!snap?.onboarding_completed) return;
  // The server already delivered a message to this player — nothing to ask.
  if (verified) return;

  if (!canUseWriteAccessApi() || allowsWriteToPm()) return;

  const last = snap?.write_access_last_prompted_at_ms ?? 0;
  if (last && Date.now() - last < WEEK_MS) return;

  fireRequestWriteAccess(qc, 4000);
}

/**
 * Telegram's native "allow the bot to message you?" dialog, as a promise of the
 * player's answer. Where the dialog does not exist (old clients, a browser) it
 * resolves true: the server's real send decides then (POST /user/notifications/verify).
 */
export function requestWriteAccessAsync(): Promise<boolean> {
  const wa = getWebApp();
  if (!wa?.requestWriteAccess || !canUseWriteAccessApi()) return Promise.resolve(true);
  return new Promise((resolve) => {
    try {
      wa.requestWriteAccess((granted) => resolve(!!granted));
    } catch {
      resolve(false);
    }
  });
}
